
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  CarouselNext, 
  CarouselPrevious 
} from "@/components/ui/carousel";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState, useCallback } from "react";
import { Circle, CircleDot } from "lucide-react";

export function AppShowcase() {
  const isMobile = useIsMobile();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  
  const screenshots = [
    {
      src: "/lovable-uploads/bcd4963f-8577-44aa-bc41-9022751f37de.png",
      alt: "Hour Tracker Dashboard",
      title: "User-Friendly Dashboard",
      description: "Overview of your tracked hours, earnings, and project statuses at a glance."
    },
    {
      src: "/lovable-uploads/786bc681-3b49-4efa-9e0d-1c81bf8ca3b8.png",
      alt: "Time Tracking Interface",
      title: "Simple Time Tracking",
      description: "Easily log your hours and categorize them by project and task."
    },
    {
      src: "/lovable-uploads/ce11ceb3-7e02-457d-b7f5-00aba710bac5.png",
      alt: "Project Management",
      title: "Project Management",
      description: "Organize your work into projects with custom rates and budgets."
    },
    {
      src: "/lovable-uploads/126458a5-a531-4d94-8fe3-ed8be78fe7a6.png",
      alt: "Invoice Generation",
      title: "Professional Invoices",
      description: "Generate polished invoices based on your tracked time with a single click."
    },
    {
      src: "/lovable-uploads/087e1512-f500-4386-b0d3-e438b1a28848.png",
      alt: "Analytics View",
      title: "Insightful Analytics",
      description: "Visualize your productivity and earnings with intuitive charts and graphs."
    }
  ];

  const [api, setApi] = useState<any>(null);

  const handleSelect = useCallback((emblaApi) => {
    if (emblaApi) {
      setCurrentSlide(emblaApi.selectedScrollSnap());
    }
  }, []);

  return (
    <section className="py-16 bg-white">
      <div className="container">
        <h2 className="text-3xl font-bold text-center mb-2">See Hour Tracker in Action</h2>
        
        <div className="relative px-8 md:px-12">
          <Carousel 
            className="max-w-4xl mx-auto"
            setApi={setApi}
            onSelect={handleSelect}
          >
            <CarouselContent>
              {screenshots.map((screenshot, index) => (
                <CarouselItem key={index}>
                  <div className="flex flex-col">
                    <div className="overflow-hidden rounded-lg border shadow-lg max-w-3xl mx-auto">
                      <img 
                        src={screenshot.src}
                        alt={screenshot.alt}
                        className="w-full object-cover aspect-video"
                      />
                    </div>
                    <div className="text-center mt-4">
                      <h3 className="font-semibold text-lg">{screenshot.title}</h3>
                      <p className="text-muted-foreground">{screenshot.description}</p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className={isMobile ? "left-0" : ""} />
            <CarouselNext className={isMobile ? "right-0" : ""} />
          </Carousel>
          
          {/* Pagination dots */}
          <div className="flex justify-center gap-2 mt-6">
            {screenshots.map((_, index) => (
              <button
                key={index}
                onClick={() => api?.scrollTo(index)}
                className="focus:outline-none"
                aria-label={`Go to slide ${index + 1}`}
              >
                {index === currentSlide ? (
                  <CircleDot className="h-4 w-4 text-primary" />
                ) : (
                  <Circle className="h-4 w-4 text-muted-foreground/50" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
